import { createContext, useContext, useState, useEffect } from "react";

const darkBase = {
  "--background": "36 42% 94%",
  "--foreground": "25 30% 13%",
  "--card": "40 50% 98%",
  "--card-foreground": "25 30% 13%",
  "--muted": "34 24% 88%",
  "--muted-foreground": "28 22% 22%",
  "--border": "32 20% 72%",
  "--ring": "18 58% 48%",
  "--shadow-color": "60, 50, 40",
  "--font-heading": "'DM Serif Display', serif",
  "--font-body": "'Figtree', sans-serif",
};

const themes = {
  creative: {
    name: "Normandie",
    description: "Argile & lin",
    vars: {
      ...darkBase,
      "--primary": "18 58% 48%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "85 22% 42%",
      "--accent": "36 65% 58%",
      "--accent-foreground": "25 28% 18%",
      "--t-sky": "85 22% 42%",
      "--t-yellow": "36 65% 58%",
      "--t-lime": "95 25% 45%",
      "--t-teal": "180 22% 42%",
      "--t-orange": "18 58% 48%",
      "--t-highlight": "18 58% 48%",
      "--hero-bg": "36 42% 94%",
      "--hero-fade": "hsl(36, 42%, 94%)",
      "--particle-color": "180, 140, 110",
      "--gradient-from": "18 58% 48%",
      "--gradient-to": "36 65% 58%",
    },
  },
  corporate: {
    name: "Corporate",
    description: "Navy & steel",
    vars: {
      ...darkBase,
      "--background": "220 25% 7%",
      "--card": "220 20% 11%",
      "--muted": "220 18% 13%",
      "--border": "220 15% 18%",
      "--primary": "220 70% 55%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "210 30% 55%",
      "--accent": "43 50% 55%",
      "--accent-foreground": "0 0% 100%",
      "--ring": "220 70% 55%",
      "--t-sky": "210 30% 55%",
      "--t-yellow": "43 50% 55%",
      "--t-lime": "160 40% 50%",
      "--t-teal": "190 40% 45%",
      "--t-orange": "25 55% 55%",
      "--t-highlight": "220 70% 55%",
      "--hero-bg": "220 25% 5%",
      "--hero-fade": "hsl(220, 25%, 7%)",
      "--particle-color": "140, 155, 180",
      "--shadow-color": "15, 20, 35",
      "--gradient-from": "220 70% 55%",
      "--gradient-to": "210 30% 55%",
    },
  },
  ecommerce: {
    name: "E-Commerce",
    description: "Rose & amber",
    vars: {
      ...darkBase,
      "--background": "350 20% 7%",
      "--card": "350 16% 11%",
      "--muted": "350 14% 13%",
      "--border": "350 10% 18%",
      "--primary": "350 75% 60%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "25 80% 58%",
      "--accent": "340 70% 62%",
      "--accent-foreground": "0 0% 100%",
      "--ring": "350 75% 60%",
      "--t-sky": "25 80% 58%",
      "--t-yellow": "40 85% 58%",
      "--t-lime": "150 50% 50%",
      "--t-teal": "170 45% 45%",
      "--t-orange": "25 80% 58%",
      "--t-highlight": "350 75% 60%",
      "--hero-bg": "350 20% 5%",
      "--hero-fade": "hsl(350, 20%, 7%)",
      "--particle-color": "180, 140, 150",
      "--shadow-color": "30, 15, 20",
      "--gradient-from": "350 75% 60%",
      "--gradient-to": "25 80% 58%",
    },
  },
  minimal: {
    name: "Minimal",
    description: "Monochrome",
    vars: {
      ...darkBase,
      "--background": "0 0% 6%",
      "--card": "0 0% 10%",
      "--muted": "0 0% 12%",
      "--border": "0 0% 18%",
      "--primary": "0 0% 85%",
      "--primary-foreground": "0 0% 6%",
      "--secondary": "0 0% 50%",
      "--accent": "0 0% 70%",
      "--accent-foreground": "0 0% 6%",
      "--ring": "0 0% 85%",
      "--t-sky": "0 0% 50%",
      "--t-yellow": "0 0% 65%",
      "--t-lime": "0 0% 55%",
      "--t-teal": "0 0% 45%",
      "--t-orange": "0 0% 60%",
      "--t-highlight": "0 0% 85%",
      "--hero-bg": "0 0% 4%",
      "--hero-fade": "hsl(0, 0%, 6%)",
      "--particle-color": "130, 130, 130",
      "--shadow-color": "15, 15, 15",
      "--gradient-from": "0 0% 60%",
      "--gradient-to": "0 0% 40%",
    },
  },
  warm: {
    name: "Artisan",
    description: "Burgundy & argile",
    vars: {
      "--background": "36 47% 93%",
      "--foreground": "20 44% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "20 44% 12%",
      "--muted": "36 30% 88%",
      "--muted-foreground": "25 30% 22%",
      "--border": "30 20% 72%",
      "--ring": "348 56% 27%",
      "--shadow-color": "30, 20, 15",
      "--font-heading": "'DM Serif Display', serif",
      "--font-body": "'Lato', sans-serif",
      "--primary": "348 56% 27%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "152 27% 33%",
      "--accent": "33 37% 64%",
      "--accent-foreground": "20 44% 12%",
      "--t-sky": "152 27% 33%",
      "--t-yellow": "33 37% 64%",
      "--t-lime": "152 35% 40%",
      "--t-teal": "152 27% 33%",
      "--t-orange": "33 50% 55%",
      "--t-highlight": "348 56% 27%",
      "--hero-bg": "348 40% 10%",
      "--hero-fade": "hsl(36, 47%, 93%)",
      "--particle-color": "160, 140, 130",
      "--gradient-from": "348 56% 27%",
      "--gradient-to": "152 27% 33%",
    },
  },
};

const darkOverrides = {
  "--background": "220 14% 7%",
  "--foreground": "0 0% 100%",
  "--card": "220 12% 13%",
  "--card-foreground": "0 0% 100%",
  "--muted": "220 10% 17%",
  "--muted-foreground": "220 8% 80%",
  "--border": "220 10% 26%",
  "--hero-bg": "220 14% 5%",
  "--hero-fade": "hsl(220, 14%, 7%)",
  "--shadow-color": "0, 0, 0",
};

const MODE_KEY = "lacrea-mode";

const getInitialMode = () => {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(MODE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState("creative");
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    const t = themes[theme];
    if (!t) return;
    const root = document.documentElement;
    Object.entries(t.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    if (mode === "dark") {
      Object.entries(darkOverrides).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }
    root.classList.toggle("dark", mode === "dark");
    root.style.colorScheme = mode;
  }, [theme, mode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch {}
  }, [mode]);

  const toggleMode = () => setMode((m) => (m === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes, currentTheme: themes[theme], mode, setMode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
